import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import dayjs from "dayjs";
import { Image as ImageIcon, ExternalLink } from "lucide-react";

// 2G · Story / Media workflow view (Lean-Core Pass 2)
// Read-only over the existing `media` collection. Buckets follow mandate §12.
const STAGE_LABEL = {
  inbox: "Inbox",
  identified: "Identified",
  needs_context: "Needs Context",
  consent_review: "Consent Review",
  approved: "Approved",
  scheduled: "Scheduled",
  published: "Published",
  outcome: "Outcome",
};

export default function StoryMedia() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [stage, setStage] = useState("inbox");
  useEffect(() => {
    api.get("/story-media").then(r => setData(r.data)).catch(e => setErr(e?.response?.data?.detail || e.message));
  }, []);

  if (err) return <div className="p-8 text-red-700" data-testid="story-media-error">Story/Media failed: {err}</div>;
  if (!data) return <div className="p-8 text-forest/60" data-testid="story-media-loading">Loading Story / Media…</div>;

  const current = data.buckets[stage] || [];

  return (
    <div className="space-y-6" data-testid="story-media-page">
      <header>
        <div className="overline">Command Surface</div>
        <h1 className="font-serif text-4xl mt-1">Story / Media</h1>
        <p className="text-ink-soft text-sm">{data.total} media items · canonical storage: {data.canonical_storage}</p>
      </header>

      <section className="bg-admin-surface border border-line rounded-xl p-4" data-testid="story-media-stages">
        <div className="flex flex-wrap gap-2">
          {data.stages.map(s => {
            const count = data.counts[s] || 0;
            const active = s === stage;
            return (
              <button key={s} onClick={() => setStage(s)} data-testid={`story-media-stage-${s}`}
                      className={`px-3 py-1.5 rounded-full text-xs border ${active ? "bg-forest text-bone border-forest" : "border-line hover:bg-bone"}`}>
                {STAGE_LABEL[s]} · {count}
              </button>
            );
          })}
        </div>
      </section>

      <section className="bg-admin-surface border border-line rounded-xl overflow-hidden" data-testid="story-media-bucket">
        <div className="px-5 py-3 border-b border-line bg-bone/40 overline">
          {STAGE_LABEL[stage]} · {current.length} items
        </div>
        {current.length === 0
          ? <div className="p-6 text-sm text-ink-soft italic">No items in this stage.</div>
          : <ul className="divide-y divide-line">
              {current.map(m => (
                <li key={m.id} className="px-5 py-3 flex items-center gap-3 hover:bg-bone/40" data-testid={`story-media-row-${m.id}`}>
                  <ImageIcon className="h-4 w-4 text-forest/60" strokeWidth={1.5} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{m.filename || m.id}</div>
                    <div className="text-xs text-ink-soft">
                      {m.content_type || "—"} {m.size ? ` · ${Math.round(m.size/1024)}KB` : ""} {m.category ? ` · ${m.category}` : ""}
                      {m.tags?.length ? ` · tags: ${m.tags.join(", ")}` : ""}
                    </div>
                  </div>
                  <div className="text-xs text-ink-soft">{m.created_at ? dayjs(m.created_at).format("MMM D") : ""}</div>
                </li>
              ))}
            </ul>}
      </section>

      <div className="text-xs text-ink-soft italic" data-testid="story-media-note">{data.note}</div>
    </div>
  );
}
